import { Component, OnInit, Inject, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { DomSanitizer } from '@angular/platform-browser'
import { SafeUrl } from '@angular/platform-browser/src/security/dom_sanitization_service';
import { NgbInputDatepicker, NgbTab, NgbTabset } from '@ng-bootstrap/ng-bootstrap';
import { BidMode, BidModeList } from '../../enums/bid-mode.enum';
import { SampleComponent } from '../sample/sample.component';
import { AudienceDesignerComponent } from '../audience-designer/audience-designer.component';
import { IBillingEntity } from '../../models/IBillingEntity';
import { IItem } from '../../models/IItem';
import { ICurrency } from '../../models/ICurrency';
import { IBid, Bid } from '../../models/IBid';
import { IBidReference } from '../../models/IBidReference';
import { Sample, ISample } from '../../models/ISample';
import { IResearchCategory, ResearchCategory } from '../../models/IResearchCategory';
import { PrettyPrintPipePipe } from '../../pipes/pretty-print-pipe.pipe';
import { environment } from '../../../environments/environment';
import { S1DataService } from '../../services/s1data.service';
import { FezaDataService } from '../../services/fezadata.service';
import { RouteDataService } from '../../services/routedata.services';
import { Utils } from '../../Utils';
import { IBidResponse } from '../../models/IBidResponse';
import { AuthService } from '../../services/auth-service.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { InstantiateExpr } from '@angular/compiler';
import { BidDTO } from '../../models/IBidDTO';
import { RedirectorComponent } from '../redirector/redirector.component';
import { PanelCheckTargetingModeList, PanelCheckTargetingMode } from '../../enums/panel-check-targeting-mode.enum';
import { TrackerRequestTypeList, eTrackerRequestType } from '../../enums/tracker-request-type.enum';
import { TrackerPeriodList, eTrackerPeriod } from '../../enums/tracker-period.enum';

@Component({
  selector: 'app-bid',
  templateUrl: './feasibility-request.component.html',
  styleUrls: ['./feasibility-request.component.css']
})
export class FeasiblityRequestComponent implements OnInit {
  bid: IBid;
  bidDTO: BidDTO;
  bidForm: FormGroup;
  bidModeList = BidModeList;
  buList$: Observable<IItem[]>;
  bidCurrencies$: Observable<ICurrency[]>;
  downloadJsonHref: SafeUrl;
  bidErrors: string;
  completeLabels: string[];
  activeIndex: number;
  queryBid: string;
  queryEvent: string;
  querySid: string;
  displayCurrency: string;
  correlationId: string;
  panelCheckTargetingModeList = PanelCheckTargetingModeList;
  trackerRequestTypeList = TrackerRequestTypeList;
  trackerPeriodList = TrackerPeriodList;
 
  constructor(private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private s1dataService: S1DataService,
    private fezaService: FezaDataService,
    private routeDataService: RouteDataService,
    private sanitizer: DomSanitizer,
    private auth: AuthService,
    private spinner: NgxSpinnerService) {

    this.route.queryParams.subscribe(params => {
      this.queryBid = params['bidId'];
      this.queryEvent = params['event'];
      this.querySid = params['sid'];
    });

  }

  ngOnInit() {
    this.bidDTO = new BidDTO();
    this.setCorrelationId();
    this.activeIndex = 0;
    this.completeLabels = [];
    this.routeDataService.routedBidRequestValue.subscribe(bid => {
      this.bid = bid || new Bid();
      this.createForm();
      this.buList$ = this.s1dataService.getBusinessUnits();
      this.bidCurrencies$ = this.s1dataService.getCurrencies();
    });

    this.fezaService.GetPanelCheckTargetingMode().subscribe(newValue => {
      this.bidForm.controls["panelCheckTargetingMode"].setValue(newValue);
    });
    if (this.queryBid != null && this.queryBid.length > 0) {
        //we have a bid Id on the URL, so we need to get the data from Feza web service and then show the response.
        this.fezaService.GetResponse(this.queryBid, this.displayCurrency).subscribe(bidResponse => {
          if (bidResponse && bidResponse.request)
            this.loadBid(bidResponse.request);
        });
      }
    }

  createForm() {
    var bidRef: IBidReference = { client: "FizzyUi", email: this.auth.Email };
    this.bidForm = this.fb.group({
      bidId: [this.bid.bidId || Utils.generateUUID()],
      bidName: [this.bid.bidName, Validators.required],
      bidExpirationDate: [this.bid.bidExpirationDate],
      bidMode: this.bidModeList.find(x => x.name === this.bid.bidMode) ? this.bidModeList.find(x => x.name === this.bid.bidMode).name : this.bidModeList[BidMode.Precommissioning].name,
      billingEntity: [this.bid.billingEntity, Validators.required],
      bidCurrency: [this.bid.bidCurrency, Validators.required],
      testMode: [this.bid.testMode],
      feasibilities: this.fb.array([]),
      bidReference: [bidRef],
      bidSupportEmail: [this.bid.bidSupportEmail],
      standardProduct: [this.bid.standardProduct],
      jobNumberInternal: [this.bid.jobNumberInternal],
      showClicks: [this.bid.showClicks],
      showOverlap: [this.bid.showOverlap],
      useResponseCache: [this.bid.useResponseCache],
      panelCheckTargetingMode: this.bid.panelCheckTargetingMode ? this.bid.panelCheckTargetingMode : this.panelCheckTargetingModeList[PanelCheckTargetingMode.Targeted].name,
      trackerRequestType: this.trackerRequestTypeList.find(x => x.name === this.bid.trackerRequestType) ? this.trackerRequestTypeList.find(x => x.name === this.bid.trackerRequestType).name : null,
      trackerPeriod: this.trackerPeriodList.find(x => x.name === this.bid.trackerPeriod) ? this.trackerPeriodList.find(x => x.name === this.bid.trackerPeriod).name : null,
      waves: [this.bid.waves],
      budgetInSingleWave: [this.bid.budgetInSingleWave],
      skipOverlaps: [this.bid.skipOverlaps],
    });
    this.createSubSampleForms(this.bid.feasibilities);
    this.bidForm.valueChanges.subscribe(val => this.updateDownloadUrl());
    this.onSetActiveSampleIndex();
    this.setCorrelationId();
  }

  setCorrelationId(id: string = null) {
    this.correlationId = id || Utils.generateUUID();
  }

  getFeasibilitiesControls() {
    var formArray: FormArray = this.bidForm.controls['feasibilities'] as FormArray;
    return formArray.controls;
  }

  getFeasibilities(index: number) {
    return this.bid.feasibilities[index];
  }

  onAddNewSample() {
    let newId = 1 + Math.max.apply(Math, this.bid.feasibilities.map(x => x.id));
    var formArray: FormArray = this.bidForm.controls['feasibilities'] as FormArray;
    var newBid = new Sample(newId);
    this.bid.feasibilities.push(newBid);
    formArray.push(this.createSubSampleForm(newBid));
    this.onSetActiveSampleIndex();
  }

  onSetActiveSampleIndex(index: number = -1) {
    if (index == -1)
      index = this.bid.feasibilities.length - 1;
    this.activeIndex = index;
  }

  onDeleteSample(id: number) {
    var index = this.bid.feasibilities.findIndex(x => x.id == id);
    var formArray: FormArray = this.bidForm.controls['feasibilities'] as FormArray;
    formArray.removeAt(index);
    this.bid.feasibilities.slice(index, 1)
    if (index > this.activeIndex)
      this.onSetActiveSampleIndex();
  }

  onSubmitBid() {
    //should probably save the bid & then take them to a page to poll for a response...
    //and do other stuff
    var bid = this.bidForm.value as IBid;
    this.routeDataService.routedBidRequestValue = Observable.of(bid);

    this.bidErrors = null;
    this.spinner.show();
    this.fezaService.ProcessfeasibilityRequest(bid, this.correlationId).subscribe(resp => {
      this.routeDataService.routedBidResponseValue = Observable.of(resp);
      this.showResponse();
    },
      err => {
        this.spinner.hide();
        if (err.error && err.error.errors)
          this.bidErrors = `Error response from feza API!<br/> ${Utils.getErrorDetailsString(err)} bidId:${bid.bidId}`;
        else
          if (err.error) this.bidErrors = `Error response from feza API! <br/> ${err.error}`;
          else
            this.bidErrors = err.message || "Error checking feasiblity!";
      });
  }

  updateBidDTO(bid: IBid) {
    this.bidDTO.bidId = bid.bidId;
    this.bidDTO.testMode = bid.testMode;
    this.bidDTO.bidMode = bid.bidMode;
    this.bidDTO.bidName = bid.bidName;
    this.bidDTO.iISSampleSupplierCurrency = bid.bidCurrency;
    this.bidDTO.internalJobBookNumber = bid.jobNumberInternal;
    this.bidDTO.activeDirectoryUserName = this.auth.User;
  }

  adRedirectMissingError(bid: BidDTO): string {
    if ((bid.iISSampleSupplierCurrency || "") === "")
      return "Missing supplier currency";

    if ((bid.internalJobBookNumber || "") === "")
      return "Missing internal job book number";

    if ((bid.iISSampleSupplierBU || "") === "")
      return "Missing supplier bu";

    return null;
  }

  onADRedirect() {
    var bid = this.bidForm.value as IBid;
    this.updateBidDTO(bid);
    if (this.adRedirectMissingError(this.bidDTO) !== null) {
      this.bidErrors = `AD redirect:${this.adRedirectMissingError(this.bidDTO)}`;
      return;
    }
    this.routeDataService.routedBidRequestValue = Observable.of(bid);
    this.bidErrors = null;
    this.spinner.show();

    console.log(`BIDDTO : ${JSON.stringify(this.bidDTO)}`);
    this.fezaService.AdRedirect(this.bidDTO, this.correlationId).subscribe(res => {
      this.spinner.hide();
      console.log(`AdUrl: ${res.audienceDesignerUrl}`);
      window.open(res.audienceDesignerUrl, '_blank');
    },
      err => {
        this.spinner.hide();
        //error in /bid redirect call
        if (err.error && err.error.errors)
          this.bidErrors = `Errors from feza:${Utils.getErrorDetailsString(err)} correlationId:${this.correlationId}`;
        else
          this.bidErrors = "Something didn't work right in /bid call..."
      });
  }

  showResponse() {
    this.spinner.hide();
    let url = "/response?";
    if (this.queryEvent) url += `event=${this.queryEvent}`;
    if (this.querySid && !this.queryEvent) url += `sid=${this.querySid}`;
    url += `bidId=${this.bid.bidId}`;
    this.router.navigateByUrl(url);
  }


  createSubSampleForms(samples: ISample[]) {
    for (var sample of samples) {
      var formArray: FormArray = this.bidForm.controls['feasibilities'] as FormArray;
      formArray.push(this.createSubSampleForm(sample));
    }
  }

  createSubSampleForm(sample: ISample): FormGroup {
    var grp = this.fb.group({
      feasibilityId: [sample.feasibilityId || Utils.generateUUID()],
      countryCode: [sample.countryCode, Validators.required],
      countryCultures: new FormArray(sample.countryCultures.map(x => new FormControl(x))),
      _hoursInField: [sample._hoursInField],
      _daysInField: [sample._daysInField],
      hoursInField: [sample._hoursInField + (sample._daysInField * 24), Validators.required],
      flexibleFieldDuration: [sample.flexibleFieldDuration],
      fieldStartDate: [sample.fieldStartDate],
      lengthOfInterview: [sample.lengthOfInterview, Validators.required],
      sampleCategoryId: [sample.sampleCategoryId, Validators.required],
      sampleSubCategoryId: [sample.sampleSubCategoryId],
      researchType: [sample.researchType, Validators.required],
      researchCategories: new FormArray(sample.researchCategories.map(x => new FormControl(x))),
      collectPII: [sample.collectPII],
      facialCoding: [sample.facialCoding],
      allowMobile: [sample.allowMobile],
      allowTablet: [sample.allowTablet],
      allowDesktop: [sample.allowDesktop],
      quotaTables: [sample.quotaTables],
      cpi: [{ "value": 0.0, "currency": "" }],
      abandonRatePercent: [sample.abandonRatePercent || "0"],
      sniffoutRatePercent: [sample.sniffoutRatePercent || "0"],
      target: [sample.target],
      numberOfCompletes: [sample.numberOfCompletes],
      incidenceRatePercent: [sample.incidenceRatePercent],
      quotaAllocationMode: [sample.quotaAllocationMode],
      feasibilityCheckSupplierMode: [sample.feasibilityCheckSupplierMode],
      calculateMaxBlend: [sample.calculateMaxBlend],
      technologyCoefficient: [sample.technologyCoefficient],
      numberOfAdditionalCustomVariables: [sample.numberOfAdditionalCustomVariables],
      interlocked: [sample.interlocked],
      eliminationMonths: [sample.eliminationMonths],
      totalTrackerNumberOfCompletes: [sample.totalTrackerNumberOfCompletes],
      waveNumberOfCompletes: [sample.waveNumberOfCompletes],
      sampleOneApiCompatible : [sample.sampleOneApiCompatible]
    });
    return grp;
  }

  updateDownloadUrl() {
    //avoid extra work if the form isn't yet valid
    if (this.bidForm.valid) {
      var theJSON = JSON.stringify(this.bidForm.value);
      var uri = this.sanitizer.bypassSecurityTrustUrl("data:text/json;charset=UTF-8," + encodeURIComponent(theJSON));
      this.downloadJsonHref = uri;
    }
  }

  onDateSelected(event) {
    this.bidForm.patchValue({ "bidExpirationDate": "" + event.year + "-" + event.month + "-" + event.day + "T00:00:00.000" });
  }

  onFileChange(event) {
    let reader = new FileReader();
    if (event.target.files && event.target.files.length > 0) {
      let file = event.target.files[0];
      reader.readAsText(file);
      reader.onload = () => {
        var jsontext = reader.result.toString();
        var bidRequest = JSON.parse(jsontext) as IBid;
        if (bidRequest.bidName) {
          this.loadBid(bidRequest);
        }
        else {
          var bidResponse = JSON.parse(jsontext) as IBidResponse
          if (bidResponse.request.bidName) {
            this.loadBid(bidResponse.request);
          }
        }
      };
    }
  }

  loadBid(bid: IBid) {
    this.bid = bid;
    this.routeDataService.routedBidRequestValue = Observable.of(this.bid);
    this.createForm();
  }


  onLoadTestFile() {
    this.fezaService.getTestBid().subscribe(bid => {
      this.routeDataService.routedBidRequestValue = Observable.of(bid);
      var d1 = Date.now();
      this.fezaService.ProcessfeasibilityRequest(bid, this.correlationId).subscribe(resp => {
        this.routeDataService.routedBidResponseValue = Observable.of(resp);
        this.showResponse();
      });
    }, err => {
      this.bidErrors = err;
    });
  }

  onLoadTestResponse() {
    this.fezaService.getTestBidResponse().subscribe(resp => {
      this.routeDataService.routedBidResponseValue = Observable.of(resp);
      this.spinner.hide();
      this.router.navigateByUrl("/response");
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/components/feasibility-request/feasibility-request.component.ts